import { Box, Grid } from '@dextech-ai/dex-design-system';
import { ReactComponent as IconDataElementConsumeOnBehalf } from '@/assets/svgs/data-element-consume-on-behalf.svg';
import { ReactComponent as IconDataElementConsume } from '@/assets/svgs/data-element-consume.svg';
import { ReactComponent as IconDataElementProvideOnBehalf } from '@/assets/svgs/data-element-provide-on-behalf.svg';
import { ReactComponent as IconDataElementProvide } from '@/assets/svgs/data-element-provide.svg';
import { ReactComponent as IconDataExchange } from '@/assets/svgs/icon-data-exchange.svg';
import { AgentPrincipalOrganisationUseCaseStatus } from '@/constants/use-case/agent-principal';
import { isAgentPrincipalEnabled } from '@/hooks/use-use-cases/index';
import { useNavigate } from "@/lib/router-dom";
import rem from '@/utils/rem';
import { useEffect, useState } from "react";
import Button from "../Button";
import CardConfigure from "@/componentsDS/Modal/CardConfigure";
import Modal from "@/componentsDS/Modal/Modal";
import SelectorBox from "../SelectorBox5/index";
import { feature } from '@/utils/feature-verifier';
import { featureConstant } from '@/constants/configuration/feature'

const DataElementSharingModal2 = ({ isVisible, hideModal, useCaseId, useCaseAgentStatus = AgentPrincipalOrganisationUseCaseStatus.DISABLED, principalCount = 0 }) => {
	const [dataElementInitiators, setDataElementInitiators] = useState([
		{ id: "provider", label: "Contribute Data", description: "You control the right to share your data with other participant, using yours or another participant's pitstop.", icon: IconDataElementProvide, $selected: false, type: 'sharing', available: true },
		{ id: "consumer", label: "Consume Data", description: "You can send requests to Contributors to obtain their data. You can use yours or another participant's pitstop.", icon: IconDataElementConsume, $selected: false, type: 'receiving', available: true },
		{ id: "provide_on_behalf", label: "Provide On Behalf", description: "You enable your pitstop to be nominated by other Contributors who want to share data.", icon: IconDataElementProvideOnBehalf, $selected: false, type: 'sharing', available: true },
		{ id: "consume_on_behalf", label: "Consume On Behalf", description: "You enable your pitstop to be nominated by other Consumers who want to receive data.", icon: IconDataElementConsumeOnBehalf, $selected: false, type: 'receiving', available: true }
	])

	/*=================================================
		General
	/================================================*/

	const { goTo } = useNavigate()

	/*=================================================
		API
	/================================================*/

	useEffect(async () => {

		let agentPrincipalOptions = [{
			id: "consume_on_behalf_of_principal",
			label: "Consume On Behalf (Principal)",
			description: "You can send data requests on behalf of your Principal(s) to Contributors to obtain their data. You can only use your own pitstop to receive the data.",
			icon: IconDataElementConsumeOnBehalf,
			$selected: false,
			type: 'receiving',
			available:  getDETypeAvailability(useCaseAgentStatus, principalCount)
		}];
		if (feature(featureConstant.AGENT_PRINCIPAL_PHASE_2)) {
			agentPrincipalOptions.push({
				id: "provide_on_behalf_of_principal",
				label: "Provide On Behalf (Principal)",
				description: "You can send data requests on behalf of your Principal(s) to Consumers to provide their data. You can only use your own pitstop to provide the data.",
				icon: IconDataElementProvideOnBehalf,
				$selected: false,
				type: 'sharing',
				available: getDETypeAvailability(useCaseAgentStatus, principalCount)
			});
		}
		const newObjElement = [...dataElementInitiators, ...agentPrincipalOptions].reduce((el, cur, index) => {
			el[cur.id] = { index: cur };
			return el;
		}, {});

		const newDataElement = Object.values(newObjElement).sort((a, b) => a.index - b.index).map(({ index: val }) => val)
		setDataElementInitiators(newDataElement);
	}, [principalCount, useCaseAgentStatus])

	function handleProceed() {
		const type = dataElementInitiators.filter(item => item.$selected === true).map(item => item.id)[0]
		goTo(`/use-cases/${useCaseId}/enrolment/${type}`)
	}

	function handleUpdateDataElementSharingSelect(item) {

		if (item.available === 'noAgent' || item.available === 'comingSoon') return

		const index = dataElementInitiators.findIndex(
			(dataElement) => dataElement.id === item.id
		);
		const updatedItems = [...dataElementInitiators];
		updatedItems.forEach(
			(dataElementInitiator) => (dataElementInitiator.$selected = false)
		);
		updatedItems[index].$selected = !updatedItems[index].$selected;
		setDataElementInitiators(updatedItems);
	};

	function getDETypeAvailability(useCaseAgentStatus, principalCount) {
		if (!isAgentPrincipalEnabled(useCaseAgentStatus)) {
			return 'noAgent';
		}
		return principalCount === 0 ? 'noPrincipals' : true;
	}

	return (
		<Modal visible={isVisible} variant='large' close={hideModal}>
			<CardConfigure >
				<CardConfigure.Header>
					<CardConfigure.Header.Title>
						<div className="card-configure__header__left">
							<div className="card-configure__title">
								<div className="card-configure__title__left">
									<IconDataExchange />
								</div>
								<div className="card-configure__title__right">
									<div className="card-configure__title__right__top">
										Initiate Data Element Sharing
									</div>
								</div>
							</div>
						</div>
					</CardConfigure.Header.Title>
				</CardConfigure.Header>
				<CardConfigure.Body>
					<CardConfigure.Body.Col>
						<div>
							<div className="data-element-sharing2__text">
								Select a sharing type
							</div>
							<Grid columns={{ xs: '1fr', sm: 'repeat(2, 1fr)' }} columnGap={2} alignItems="start">
								<Box display="flex" direction="column" gap={2}>
									<div className="data-element-sharing2__small-text">
										Sharing
									</div>
									{dataElementInitiators.map(
										(dataElementInitiate, index) => (
											dataElementInitiate.type === 'sharing' &&
											<SelectorBox
												id={dataElementInitiate?.id}
												key={dataElementInitiate?.id}
												label={dataElementInitiate?.label}
												description={
													dataElementInitiate?.description
												}
												active={
													dataElementInitiate?.$selected
												}
												Icon={dataElementInitiate?.icon}
												available={dataElementInitiate?.available}
												submit={() =>
													handleUpdateDataElementSharingSelect(
														dataElementInitiate
													)
												}
											/>
										)
									)}
								</Box>

								<Box display="flex" direction="column" gap={2}>
									<div className="data-element-sharing2__small-text">
										Receiving
									</div>
									{dataElementInitiators.map(
										(dataElementInitiate, index) => (
											dataElementInitiate.type === 'receiving' &&
											<SelectorBox
												id={dataElementInitiate?.id}
												key={dataElementInitiate?.id}
												label={dataElementInitiate?.label}
												description={
													dataElementInitiate?.description
												}
												active={
													dataElementInitiate?.$selected
												}
												Icon={dataElementInitiate?.icon}
												available={dataElementInitiate?.available}
												submit={() =>
													handleUpdateDataElementSharingSelect(
														dataElementInitiate
													)
												}
											/>
										)
									)}
								</Box>
							</Grid>
						</div>
					</CardConfigure.Body.Col>
				</CardConfigure.Body>
				<CardConfigure.Footer>
					<CardConfigure.Footer.Right>
						<Button
							text="Cancel"
							addClass="add-data-element-cancel-button"
							data-testid="close-button"
							ghost
							noborder
							submit={hideModal}
						/>
						<Button
							text="Proceed"
							addClass="add-data-element-proceed-button"
							round
							data-testid="proceed-button"
							submit={handleProceed}
							disabled={
								!dataElementInitiators?.some(
									(initiator) => (initiator.$selected === true ? initiator.available === 'noPrincipals' ? false : true : false)
								)
							}
						/>
					</CardConfigure.Footer.Right>
				</CardConfigure.Footer>
			</CardConfigure>
		</Modal>
	);
};

export default DataElementSharingModal2;
